<template>
  <section>
    <div class="flex items-start justify-start mb-4">
      <div class="w-1/2 flex flex-col gap-2">
        <div v-for="item in REPORT_TYPES" :key="item.id" class="flex items-center gap-2">
          <RadioButton
            :inputId="item.id"
            :value="item.value"
            v-model="reportType"
            name="reportTypes"
          />
          <LabelDefault :forInput="item.id">{{ item.label }}</LabelDefault>
        </div>
      </div>
      <div class="w-1/2 flex flex-col gap-2">
        <div v-for="item in GROUPS" :key="item.id" class="flex items-center gap-2">
          <RadioButton
            :inputId="item.id"
            :value="item.value"
            v-model="groupedBy"
            name="groupBy"
          />
          <LabelDefault :forInput="item.id">{{ item.label }}</LabelDefault>
        </div>
      </div>
    </div>
    <LabelDefault> Date Range </LabelDefault>
    <DatePicker
      id="report-date-picker"
      class="mb-3"
      v-model="reportRangeAsDate"
      selectionMode="range"
      :manualInput="false"
    />
    <ReportDownloadAction
      :reportInCreationStatus="reportInCreationStatus"
      :isGeneratingReport="isGeneratingReport"
      :reportLink="reportInCreationLink"
      @generateReport="generateReport"
      @afterReportDownloaded="afterReportDownloaded"
    />
  </section>
</template>
<script setup lang="ts">
import moment from 'moment'
import DatePicker from 'primevue/datepicker'
import RadioButton from 'primevue/radiobutton'
import { computed, ref } from 'vue'

import LabelDefault from '@/common/components/atoms/LabelDefault.vue'
import { DateRangeISO, useDateRange } from '@/common/composables/useDatetime'
import { Option } from '@/common/interfaces/Option'
import ReportDownloadAction from '@/modules/report/components/molecules/ReportDownloadAction.vue'
import { useGenerateReport } from '@/modules/report/composables/useGenerateReport'

import { IsoDate, ReservationExportArgs } from '@generated/types'

type ReportTypeValue = 'transaction' | 'reservation'

const TODAY = moment()
const dateRange = ref<DateRangeISO>([
  TODAY.clone().startOf('month').format('yyyy-MM-DD') as IsoDate,
  TODAY.clone().format('yyyy-MM-DD') as IsoDate,
])
const { dateRange: reportRangeAsDate } = useDateRange(dateRange, value => (dateRange.value = value))
const startDate = computed(() => dateRange.value![0] as IsoDate)
const endDate = computed(() => dateRange.value![1] as IsoDate)

const REPORT_TYPES: Option<ReportTypeValue>[] = [
  { value: 'transaction', label: 'Transaction', id: 'transaction' },
  { value: 'reservation', label: 'Reservation', id: 'reservation' },
]
const reportType = ref<ReportTypeValue>(REPORT_TYPES[0].value)

const GROUPS: Option<string>[] = [
  { id: 'by-created', label: 'By Created', value: 'byCreated' },
  { id: 'by-performance', label: 'By Performance', value: 'byPerformance' },
]
const groupedBy = ref<string>(GROUPS[0].value)

const reservationExportArgs = computed<ReservationExportArgs>(function () {
  if (groupedBy.value === 'byCreated') {
    return { start_date: startDate.value, end_date: endDate.value }
  }
  return { show_start_date: startDate.value, show_end_date: endDate.value }
})
const {
  afterReportDownloaded,
  generateReport,
  reportInCreationLink,
  isGeneratingReport,
  reportInCreationStatus,
} = useGenerateReport(reportType, reservationExportArgs)
</script>
